import React, { useEffect, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Receipt, Plus, PencilSimple, Trash, CircleNotch, X, CheckCircle } from "@phosphor-icons/react";

/**
 * Actual Disposal panel — lives on the dashboard next to Estimated Disposal.
 * Lets admins log real dump tickets (date, tons, price/ton) and see today /
 * this-week / this-month totals. Editable list of all tickets below the cards.
 */

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const tonsFmt = (n) => (n == null ? "—" : `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })} T`);
const todayISO = () => new Date().toISOString().slice(0, 10);

function Card({ label, main, sub, testid }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-sm p-3" data-testid={testid}>
      <div className="text-[9px] font-bold uppercase tracking-[0.28em] text-[#CFAB7A]">{label}</div>
      <div className="mt-1 font-heading font-black text-lg text-white">{main}</div>
      {sub && <div className="text-[10px] text-white/60 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function ActualDisposalPanel({ estimatedDisposal = null, readOnly = false }) {
  const [summary, setSummary] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // ticket obj or null
  const [saving, setSaving] = useState(false);
  // Day filter — YYYY-MM-DD string. When set, we show only tickets whose
  // dump_date matches. Empty string means "show all recent tickets".
  const [dayFilter, setDayFilter] = useState("");

  const [form, setForm] = useState({
    dump_date: todayISO(),
    tons: "",
    price_per_ton: "",
    total_cost: "",
    facility: "",
    ticket_number: "",
    notes: "",
    _overrideTotal: false,
  });

  async function reload() {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get("/disposal-tickets/summary"),
        api.get("/disposal-tickets", { params: { limit: 100 } }),
      ]);
      setSummary(s.data);
      setTickets(t.data.tickets || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load disposal tickets");
    } finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, []);

  const autoTotal = (() => {
    const t = parseFloat(form.tons); const p = parseFloat(form.price_per_ton);
    if (isFinite(t) && isFinite(p)) return (t * p).toFixed(2);
    return "";
  })();

  function openNew() {
    setEditing(null);
    setForm({ dump_date: todayISO(), tons: "", price_per_ton: "", total_cost: "", facility: "", ticket_number: "", notes: "", _overrideTotal: false });
    setShowForm(true);
  }
  function openEdit(t) {
    setEditing(t);
    setForm({
      dump_date: t.dump_date,
      tons: String(t.tons ?? ""),
      price_per_ton: String(t.price_per_ton ?? ""),
      total_cost: String(t.total_cost ?? ""),
      facility: t.facility || "",
      ticket_number: t.ticket_number || "",
      notes: t.notes || "",
      _overrideTotal: !!t.total_cost_overridden,
    });
    setShowForm(true);
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.dump_date) { toast.error("Dump date is required"); return; }
    if (!form.tons || parseFloat(form.tons) <= 0) { toast.error("Tons must be > 0"); return; }
    if (form.price_per_ton === "") { toast.error("Price / ton is required"); return; }
    setSaving(true);
    try {
      const payload = {
        dump_date: form.dump_date,
        tons: parseFloat(form.tons),
        price_per_ton: parseFloat(form.price_per_ton),
        facility: form.facility || null,
        ticket_number: form.ticket_number || null,
        notes: form.notes || null,
      };
      if (form._overrideTotal && form.total_cost !== "") {
        payload.total_cost = parseFloat(form.total_cost);
      } else if (!form._overrideTotal && editing?.total_cost_overridden) {
        // User cleared the override — send null to force auto-recompute path.
        payload.total_cost = null;
      }
      if (editing) {
        await api.patch(`/disposal-tickets/${editing.id}`, payload);
        toast.success("Ticket updated");
      } else {
        await api.post("/disposal-tickets", payload);
        toast.success("Ticket saved");
      }
      setShowForm(false);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function remove(t) {
    if (!window.confirm(`Delete disposal ticket from ${t.dump_date}?`)) return;
    try {
      await api.delete(`/disposal-tickets/${t.id}`);
      toast.success("Ticket deleted");
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  }

  return (
    <div className="bg-[#0a2a48] text-white rounded-sm p-4 space-y-4" data-testid="actual-disposal-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-sm bg-[#2E7D32] grid place-items-center"><Receipt size={16} weight="bold" /></div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#7CC982] font-bold">Actual Disposal (Real Tickets)</div>
            <div className="text-xs text-white/70">Enter real dump tickets. Reports run daily / weekly / monthly.</div>
          </div>
        </div>
        {!readOnly && (
          <button onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#CFAB7A] text-[#012340] text-xs font-bold uppercase tracking-[0.2em] hover:brightness-110" data-testid="add-disposal-ticket">
            <Plus size={14} weight="bold" /> Add Ticket
          </button>
        )}
      </div>

      {/* Rolling summary cards */}
      {loading ? (
        <div className="text-xs text-white/60"><CircleNotch className="animate-spin inline mr-2" /> Loading…</div>
      ) : (
        <div className="grid grid-cols-3 gap-2" data-testid="disposal-summary-cards">
          <Card testid="dt-today"      label="Today"      main={money(summary?.today?.total_cost)}      sub={`${tonsFmt(summary?.today?.tons)} · ${summary?.today?.count || 0} tix`} />
          <Card testid="dt-week"       label="This Week"  main={money(summary?.this_week?.total_cost)}  sub={`${tonsFmt(summary?.this_week?.tons)} · avg ${money(summary?.this_week?.avg_cost_per_ton)}/T`} />
          <Card testid="dt-month"      label="This Month" main={money(summary?.this_month?.total_cost)} sub={`${tonsFmt(summary?.this_month?.tons)} · avg ${money(summary?.this_month?.avg_cost_per_ton)}/T`} />
        </div>
      )}

      {/* Delta vs Estimated (only shown when Dashboard passes estimatedDisposal) */}
      {!loading && estimatedDisposal != null && summary?.this_month && (() => {
        const est = Number(estimatedDisposal) || 0;
        const act = Number(summary.this_month.total_cost) || 0;
        const delta = act - est;
        const pct = est ? (delta / est) * 100 : null;
        const higher = delta > 0;
        return (
          <div
            className="bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-[11px] flex flex-wrap items-center gap-2"
            data-testid="disposal-delta-row"
          >
            <span className="text-white/70">This month —</span>
            <span>Estimated <b className="text-white">{money(est)}</b></span>
            <span className="text-white/40">·</span>
            <span>Actual <b className="text-white">{money(act)}</b></span>
            <span className="text-white/40">·</span>
            <span
              className={`font-bold ${higher ? "text-[#FFB74D]" : "text-[#7CC982]"}`}
              data-testid="disposal-delta-value"
            >
              Δ {money(delta)}{pct != null ? ` (${higher ? "+" : ""}${pct.toFixed(1)}%)` : ""}
            </span>
            <span className="ml-auto text-[9px] uppercase tracking-[0.22em] text-white/50">
              {higher ? "Actual > Estimated" : delta < 0 ? "Actual < Estimated" : "Matched"}
            </span>
          </div>
        );
      })()}

      {/* Recent tickets table — scrollable window (~5 rows) with a day
          filter at the top so admins can jump to a specific dump date. */}
      {(() => {
        const filtered = dayFilter
          ? tickets.filter((t) => t.dump_date === dayFilter)
          : tickets;
        return (
      <div className="bg-white/5 border border-white/10 rounded-sm overflow-hidden">
        <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#CFAB7A]">
            Recent Tickets ({filtered.length}{dayFilter ? ` of ${tickets.length}` : ""})
          </span>
          <div className="ml-auto flex items-center gap-1.5">
            <label className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-bold">Search day</label>
            <input
              type="date"
              value={dayFilter}
              onChange={(e) => setDayFilter(e.target.value)}
              className="bg-[#012340] border border-white/15 rounded-sm px-2 py-1 text-[11px] text-white font-mono focus:border-[#CFAB7A] outline-none"
              data-testid="disposal-day-filter"
            />
            {dayFilter && (
              <button
                type="button"
                onClick={() => setDayFilter("")}
                className="text-[10px] text-[#CFAB7A] hover:text-white uppercase tracking-[0.18em] font-bold"
                data-testid="disposal-day-clear"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        {filtered.length === 0 ? (
          <div className="p-4 text-xs text-white/60 text-center" data-testid="disposal-empty">
            {dayFilter ? `No tickets on ${dayFilter}.` : "No tickets yet — click \u201cAdd Ticket\u201d to log the first dump."}
          </div>
        ) : (
          <ul className="max-h-[230px] overflow-y-auto" data-testid="disposal-tickets-list">
            {filtered.map((t) => (
              <li key={t.id} className="px-3 py-2 border-t border-white/5 first:border-t-0 flex items-center gap-3 text-xs hover:bg-white/5" data-testid={`ticket-row-${t.id}`}>
                <span className="text-white/70 w-24 flex-shrink-0 font-mono">{t.dump_date}</span>
                <span className="w-20 flex-shrink-0 text-right">{tonsFmt(t.tons)}</span>
                <span className="w-24 flex-shrink-0 text-right text-white/70">@ {money(t.price_per_ton)}</span>
                <span className="w-24 flex-shrink-0 text-right font-bold">{money(t.total_cost)}{t.total_cost_overridden && <span className="ml-1 text-[9px] text-[#CFAB7A]">MANUAL</span>}</span>
                <span className="flex-1 min-w-0 truncate text-white/60">
                  {t.facility || "—"}{t.ticket_number ? ` · #${t.ticket_number}` : ""}
                  {t.submitted_by_role === "driver" && <span className="ml-1 text-[9px] text-[#7FC683]">DRIVER</span>}
                </span>
                <button onClick={() => openEdit(t)} className="p-1 text-white/60 hover:text-white" data-testid={`ticket-edit-${t.id}`} disabled={readOnly} style={readOnly ? { display: "none" } : undefined}><PencilSimple size={12} weight="bold" /></button>
                <button onClick={() => remove(t)} className="p-1 text-white/60 hover:text-[#E57373]" data-testid={`ticket-del-${t.id}`} disabled={readOnly} style={readOnly ? { display: "none" } : undefined}><Trash size={12} weight="bold" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
        );
      })()}

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setShowForm(false)} data-testid="disposal-form-modal">
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white text-[#012340] rounded-sm w-full max-w-md p-5 space-y-3">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-heading font-black text-lg">{editing ? "Edit Disposal Ticket" : "New Disposal Ticket"}</h2>
              <button type="button" onClick={() => setShowForm(false)} className="text-[#5C738A] hover:text-[#012340]"><X size={16} weight="bold" /></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="col-span-2">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A] mb-1">Dump Date *</div>
                <input type="date" required value={form.dump_date} onChange={(e) => setForm((f) => ({ ...f, dump_date: e.target.value }))} className="w-full px-3 py-2 border border-[#012340]/20 rounded-sm text-sm" data-testid="dt-date" />
              </label>
              <label>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A] mb-1">Tons *</div>
                <input type="number" step="0.01" min="0" required value={form.tons} onChange={(e) => setForm((f) => ({ ...f, tons: e.target.value }))} className="w-full px-3 py-2 border border-[#012340]/20 rounded-sm text-sm" data-testid="dt-tons" />
              </label>
              <label>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A] mb-1">Price / Ton *</div>
                <input type="number" step="0.01" min="0" required value={form.price_per_ton} onChange={(e) => setForm((f) => ({ ...f, price_per_ton: e.target.value }))} className="w-full px-3 py-2 border border-[#012340]/20 rounded-sm text-sm" data-testid="dt-ppt" />
              </label>
              <label className="col-span-2">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A] mb-1 flex items-center gap-2">
                  Total Cost <span className="font-normal normal-case tracking-normal text-[#5C738A]">(auto: {money(autoTotal || 0)})</span>
                  <label className="ml-auto text-[10px] font-normal normal-case tracking-normal cursor-pointer">
                    <input type="checkbox" checked={form._overrideTotal} onChange={(e) => setForm((f) => ({ ...f, _overrideTotal: e.target.checked, total_cost: e.target.checked ? (f.total_cost || autoTotal) : "" }))} className="mr-1" data-testid="dt-override" />
                    Manual override
                  </label>
                </div>
                <input type="number" step="0.01" min="0" disabled={!form._overrideTotal} value={form._overrideTotal ? form.total_cost : autoTotal} onChange={(e) => setForm((f) => ({ ...f, total_cost: e.target.value }))} className={`w-full px-3 py-2 border rounded-sm text-sm ${form._overrideTotal ? "border-[#CFAB7A] bg-white" : "border-[#012340]/20 bg-[#F5F5F5] text-[#5C738A]"}`} data-testid="dt-total" />
              </label>
              <label>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A] mb-1">Facility</div>
                <input type="text" value={form.facility} onChange={(e) => setForm((f) => ({ ...f, facility: e.target.value }))} placeholder="Waste Mgmt Landfill" className="w-full px-3 py-2 border border-[#012340]/20 rounded-sm text-sm" data-testid="dt-facility" />
              </label>
              <label>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A] mb-1">Ticket #</div>
                <input type="text" value={form.ticket_number} onChange={(e) => setForm((f) => ({ ...f, ticket_number: e.target.value }))} className="w-full px-3 py-2 border border-[#012340]/20 rounded-sm text-sm" data-testid="dt-ticketno" />
              </label>
              <label className="col-span-2">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A] mb-1">Notes</div>
                <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} className="w-full px-3 py-2 border border-[#012340]/20 rounded-sm text-sm" data-testid="dt-notes" />
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-sm border border-[#012340]/20 text-xs font-bold uppercase tracking-[0.2em]">Cancel</button>
              <button type="submit" disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-sm bg-[#012340] text-white text-xs font-bold uppercase tracking-[0.2em] disabled:opacity-40" data-testid="dt-submit">
                {saving ? <CircleNotch className="animate-spin" size={12} /> : <CheckCircle size={12} weight="bold" />}
                {editing ? "Save Changes" : "Save Ticket"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
